import { policyDifferences } from './canonical.js';
/**
 * A deployment's permissions boundary (IM-192): a policy the **customer**
 * creates and owns in their own account, before the apply that uses it, which
 * the installer only attaches (MP-3693). This control plane offers the right
 * document, checks that what the customer created is that document, records
 * it, and sends its ARN on every run for the instance.
 *
 * Qrvey never creates or edits the boundary. The deploy role cannot (bootstrap
 * v10, IM-193), and nothing here asks it to.
 */
/** Every boundary is named for its deployment, at path `/`, whatever the deployment's own path. */
export const BOUNDARY_POLICY_PREFIX = 'qrvey-boundary-';
/** The Terraform variable the installer reads the boundary's ARN from (MP-3619). */
export const BOUNDARY_VARIABLE = 'iam_permissions_boundary_arn';
export const boundaryPolicyName = (deploymentId) => `${BOUNDARY_POLICY_PREFIX}${deploymentId}`;
export const boundaryPolicyArn = (partition, awsAccountId, deploymentId) => `arn:${partition}:iam::${awsAccountId}:policy/${boundaryPolicyName(deploymentId)}`;
/**
 * `pattern`: every reference matched by a pattern carrying the deployment id,
 * so it is known before the first apply and does not go stale on an upgrade.
 * `exact`: a deployment from before MP-3693, whose two Karpenter roles carry
 * no id and are named by their exact, timestamped ARNs — rendered after an
 * apply, and stale if an upgrade recreates either role.
 */
export const BOUNDARY_KINDS = ['pattern', 'exact'];
export const isBoundaryKind = (value) => BOUNDARY_KINDS.includes(value);
/**
 * The id grammar for a **new** deployment (MP-3693 AC14): what the installer
 * accepts before it will attach a boundary, and what lets "the first `-` after
 * the id ends the id" hold in every role name it builds.
 *
 * Narrower than `deploymentIdProblem`'s eighteen, and only for an instance
 * that does not exist yet. An existing deployment with a longer id keeps it and
 * keeps running; it simply cannot be given a boundary. A minted id is eight
 * characters and always matches.
 */
export const NEW_DEPLOYMENT_ID_PATTERN = /^[a-z0-9]{1,16}$/;
/** What is wrong with an id for an instance that does not exist yet, if anything. */
export const newDeploymentIdProblem = (value) => {
    if (typeof value !== 'string')
        return undefined;
    const id = value.trim();
    if (id === '' || NEW_DEPLOYMENT_ID_PATTERN.test(id))
        return undefined;
    return `A new deployment's id must be 1 to 16 lowercase letters and digits (^[a-z0-9]{1,16}$), so its IAM role names leave room for the installer's own and its permissions boundary can name only its own roles. "${id}" is ${String(id.length)} characters.`;
};
export const isActiveBoundary = (mark) => mark !== undefined && mark.removedAt === undefined;
/**
 * Whether the customer's live policy is the boundary this instance expects —
 * the check `POST …/boundary` makes before it writes anything.
 *
 * The expected document is what the installer published (an existing
 * instance, or a new one after its first apply). A new instance's boundary
 * rendered from the pinned template before its first apply is slice 2, and
 * arrives as the same `PublishedBoundary` shape.
 *
 * Answers the boundary to record, or the refusal. Nothing partial: a policy at
 * the right name with the wrong content is refused naming every difference,
 * because the next apply would bound every role with it.
 */
export const checkBoundary = (deploymentId, facts) => {
    const name = boundaryPolicyName(deploymentId);
    const arn = facts.expectedArn;
    if (facts.published.state === 'unreadable') {
        return {
            refused: {
                error: 'boundary-unreadable',
                detail: `Could not read the boundary the installer published for ${deploymentId}: ${facts.published.reason}`,
            },
        };
    }
    if (facts.published.state === 'absent') {
        return {
            refused: {
                error: 'boundary-not-published',
                detail: `No permissions boundary has been published for ${deploymentId} yet. It needs one apply with an installer that publishes it (MP-3693); then this offers the policy to create.`,
            },
        };
    }
    const published = facts.published.value;
    if (published.expectedArn !== arn) {
        return {
            refused: {
                error: 'boundary-wrong-name',
                detail: `The installer expects the boundary at ${published.expectedArn}, but a deployment's boundary is ${arn}.`,
            },
        };
    }
    if (facts.live.state === 'unreadable') {
        return {
            refused: {
                error: 'boundary-unreadable',
                detail: `Could not read policy ${name} in this account: ${facts.live.reason}`,
            },
        };
    }
    if (facts.live.state === 'absent') {
        return {
            refused: {
                error: 'boundary-policy-missing',
                detail: `There is no policy ${name} at path / in this account. Create it from the document offered here, then record it.`,
            },
        };
    }
    const live = facts.live.value;
    if (live.arn !== arn) {
        return {
            refused: {
                error: 'boundary-wrong-name',
                detail: `The policy found is ${live.arn}; a deployment's boundary must be ${arn}, at path /.`,
            },
        };
    }
    const differences = policyDifferences(published.document, live.document);
    if (differences.length > 0) {
        return {
            refused: {
                error: 'boundary-mismatch',
                detail: `Policy ${name}'s default version is not the document this instance expects.`,
                differences,
            },
        };
    }
    return { ok: { ...published, arn } };
};
/**
 * Whether a run's own variables may stand, given the instance's boundary.
 *
 * The control plane sets `iam_permissions_boundary_arn` on every run for an
 * instance with a recorded boundary, as it sets the region and the state key —
 * never from the form, so a forgotten field cannot drop it and a typed one
 * cannot swap it. A run carrying its own value is refused unless that value is
 * the recorded one, which is what a form refilled from the previous run sends.
 */
export const boundaryVariableProblem = (sent, mark) => {
    if (sent === undefined || sent === null || sent === '')
        return undefined;
    if (isActiveBoundary(mark) && sent === mark.arn)
        return undefined;
    return isActiveBoundary(mark)
        ? `${BOUNDARY_VARIABLE} is set from this instance's recorded boundary, ${mark.arn}, and a run cannot name another.`
        : `${BOUNDARY_VARIABLE} is set from an instance's recorded boundary, never by a run. Record the boundary on the instance instead.`;
};
/**
 * The one-resource CloudFormation template a customer deploys to create the
 * boundary — the artifact offered beside the document and the CLI command.
 *
 * `ManagedPolicyName` is fixed and `Path` is `/`, so the policy lands at the
 * one ARN the deploy role will accept. Tagged `qrvey-id`, as every resource of
 * the deployment is, so it is found by the same tag in the customer's console.
 * No `DeletionPolicy`: it is theirs, and deleting the stack should do what
 * they expect.
 */
export const boundaryPolicyTemplate = (deploymentId, document) => ({
    AWSTemplateFormatVersion: '2010-09-09',
    Description: `Qrvey permissions boundary for deployment ${deploymentId}. Yours to own: Qrvey attaches it and never edits it.`,
    Resources: {
        Boundary: {
            Type: 'AWS::IAM::ManagedPolicy',
            Properties: {
                ManagedPolicyName: boundaryPolicyName(deploymentId),
                Path: '/',
                Description: `Permissions boundary for the IAM roles of Qrvey deployment ${deploymentId}.`,
                PolicyDocument: document,
                Tags: [{ Key: 'qrvey-id', Value: deploymentId }],
            },
        },
    },
    Outputs: {
        BoundaryArn: { Value: { Ref: 'Boundary' } },
    },
});
/**
 * The equivalent `aws iam create-policy`, for a customer who would rather not
 * deploy a stack. The document goes in as a file, because a 2.6 KB policy on a
 * command line is one shell-quoting mistake from a different policy.
 */
export const createPolicyCommand = (deploymentId) => [
    'aws iam create-policy',
    `--policy-name ${boundaryPolicyName(deploymentId)}`,
    '--path /',
    `--policy-document file://${boundaryPolicyName(deploymentId)}.json`,
    `--tags Key=qrvey-id,Value=${deploymentId}`,
].join(' \\\n  ');
//# sourceMappingURL=boundary.js.map